import { useI18nForVue2 } from '@invivodf/locales/src/useI18n'

import { ErrorInfo, ImportReportContent, ImportReportLineError } from '@invivodf/common/src/contexts/parameters'
import { productImportErrorCodes } from '@b2ag/module-product-sdk'
import {
  RecommendedListsImportReportSummaryDetailsFileErrorCodes,
  RecommendedListsImportReportSummaryDetailsLineErrorCodes,
} from '@invivodf/wishlist-sdk'

export function mapImportReportContent(report: ImportReportContent): ImportReportContent {
  return {
    ...report,
    line_errors: mapLineErrors(report.line_errors),
    file_errors: mapFileErrors(report.file_errors),
  }
}

function mapFileErrors(fileErrors: string[] | ErrorInfo[]): string[] {
  if (!fileErrors || fileErrors.length === 0) {
    return []
  }

  if (typeof fileErrors[0] === 'string') {
    return fileErrors as string[]
  }

  return (fileErrors as ErrorInfo[]).map((error) => mapErrorCodeToMessageTranslated(error))
}

function mapLineErrors(lineErrors: ImportReportLineError[]): ImportReportLineError[] {
  return lineErrors.map((lineError) => {
    if (lineError.errors && lineError.errors.length > 0) {
      const messagesFromErrorCodes = lineError.errors.map((error) => mapErrorCodeToMessageTranslated(error))

      return {
        ...lineError,
        line: lineError.line.toString(),
        messages: messagesFromErrorCodes,
      }
    }

    return lineError
  })
}

function mapErrorCodeToMessageTranslated(error: ErrorInfo): string {
  const i18n = useI18nForVue2()
  const errorCode = error.code

  const translationKey = ERROR_CODE_TRANSLATIONS[errorCode]
  if (translationKey) {
    return translateErrorMessage(error, translationKey, i18n)
  }

  return i18n.t('ctx.import.errors__generic_error', { code: errorCode }).toString()
}

function translateErrorMessage(error: ErrorInfo, translationKey: string, i18n: any): string {
  const translationParams = {
    ...error.data,
    missingHeaders: error.data?.missingHeaders?.join(', '),
    allowedValues: error.data?.allowedValues?.join(', '),
  }

  return i18n.t(translationKey, translationParams).toString()
}

const IMPORT_ERROR_CODES = [
  ...Object.values(productImportErrorCodes),
  ...Object.values(RecommendedListsImportReportSummaryDetailsFileErrorCodes),
  ...Object.values(RecommendedListsImportReportSummaryDetailsLineErrorCodes),
] as const

const ERROR_CODE_TRANSLATIONS: Record<string, string> = Object.fromEntries(
  IMPORT_ERROR_CODES.map((code) => [code, generateTranslationKey(code)]),
)

function generateTranslationKey(errorCode: string): string {
  return `ctx.import.errors__${errorCode.toLowerCase()}`
}
